import React, { type ReactNode } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { FadeInDown } from 'react-native-reanimated';
import { ThemedText } from '../themed-text';
import { cn } from '@/utils/cn';
import { AnimatedView } from './animated';
import { PressableScale } from './pressable-scale';

export interface DetailHeaderProps {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  backDisabled?: boolean;
  /** Ações à direita (use botões com rótulo, não só ícone) */
  actions?: ReactNode;
  className?: string;
}

/**
 * Cabeçalho de tela de detalhe: voltar em pill branca, título/subtítulo e
 * ações rotuladas à direita — tudo numa linha só. Substitui os botões
 * redondos só de ícone.
 */
export function DetailHeader({
  title,
  subtitle,
  onBack,
  backDisabled = false,
  actions,
  className,
}: DetailHeaderProps) {
  return (
    <AnimatedView
      entering={FadeInDown.springify().damping(20).delay(50)}
      className={cn('w-full flex-row items-center gap-3 px-5', className)}
    >
      {onBack ? (
        <PressableScale
          onPress={onBack}
          disabled={backDisabled}
          haptic
          accessibilityRole="button"
          accessibilityLabel="Voltar"
          className={cn(
            'h-10 w-10 shrink-0 items-center justify-center rounded-full bg-card',
            backDisabled && 'opacity-40',
          )}
          style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
          hitSlop={8}
        >
          <Ionicons className="text-neutral-900" name="arrow-back" size={20} />
        </PressableScale>
      ) : null}
      <View className="min-w-0 flex-1 gap-0.5">
        <ThemedText
          className="font-bold text-2xl tracking-tight text-neutral-900"
          numberOfLines={2}
        >
          {title}
        </ThemedText>
        {subtitle ? (
          <ThemedText className="text-sm leading-5 text-neutral-500" numberOfLines={2}>
            {subtitle}
          </ThemedText>
        ) : null}
      </View>
      {actions ? <View className="shrink-0 flex-row items-center gap-2">{actions}</View> : null}
    </AnimatedView>
  );
}
